import { Component, Input, HostListener } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslatePipe } from '../../../../core/pipes/translate.pipe';

@Component({
  selector: 'app-project-gallery',
  standalone: true,
  imports: [CommonModule, TranslatePipe],
  template: `
    <div
      class="surface-card rounded-3xl p-6 md:p-8 antialiased"
    >
      <div class="flex items-center justify-between mb-6 pb-2 border-b border-border-subtle">
        <div class="flex items-center gap-2">
          <div class="w-1.5 h-3.5 bg-primary rounded-sm shadow-sm"></div>
          <h2 class="text-sm font-black text-text-primary uppercase tracking-wider font-mono">
            {{ 'projectsDetails.photoGallery' | translate }}
          </h2>
        </div>
        <span
          class="px-2.5 py-1 bg-surface-elevated border border-border rounded-lg text-[10px] font-mono font-bold text-text-muted"
        >
          {{ galleryImages.length }} {{ 'projectsDetails.architecturalViews' | translate }}
        </span>
      </div>

      <!-- Grid -->
      <div class="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
        @for (image of galleryImages; track image; let idx = $index) {
          <div
            [class.md:col-span-2]="idx === 0"
            [class.md:row-span-2]="idx === 0"
            [class.aspect-[16/10]]="idx === 0"
            [class.aspect-[4/3]]="idx !== 0"
            class="w-full rounded-2xl overflow-hidden relative group cursor-pointer border border-border/80 bg-bg-secondary shadow-subtle"
            (click)="openLightbox(idx)"
          >
            <img
              [src]="image"
              [alt]="('projectsDetails.architecturalView' | translate) + ' ' + (idx + 1)"
              class="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700 ease-out antialiased"
              loading="lazy"
            />
            <div
              class="absolute inset-0 bg-gradient-to-t from-black/90 via-black/30 to-transparent backdrop-blur-[2px] opacity-0 group-hover:opacity-100 transition-all duration-300 flex items-center justify-center"
            >
              <div
                class="w-10 h-10 rounded-xl bg-surface/60 backdrop-blur-md border border-white/20 flex items-center justify-center text-white translate-y-2 group-hover:translate-y-0 transition-transform duration-300"
              >
                <i class="fa-solid fa-expand text-xs"></i>
              </div>
              <div
                class="absolute bottom-4 left-4 flex items-center gap-2 px-2.5 py-1 bg-black/60 backdrop-blur-md border border-white/10 rounded-lg"
              >
                <span class="text-[9px] font-mono font-black text-white uppercase tracking-widest">
                  {{ 'projectsDetails.view' | translate }} {{ idx + 1 }}
                </span>
              </div>
            </div>
          </div>
        }
      </div>
    </div>

    <!-- ── Lightbox ──────────────────────────────────────────── -->
    @if (lightboxOpen) {
      <div
        class="fixed inset-0 z-50 flex items-center justify-center bg-black/95 backdrop-blur-md"
        (click)="closeLightbox()"
      >
        <!-- Image wrapper -->
        <div
          class="relative flex items-center justify-center w-full h-full p-4 md:p-10"
          (click)="$event.stopPropagation()"
        >
          <img
            [src]="galleryImages[activeIndex]"
            [alt]="('projectsDetails.architecturalView' | translate) + ' ' + (activeIndex + 1)"
            class="max-w-full max-h-[85vh] object-contain rounded-2xl shadow-2xl select-none"
          />

          <!-- Counter -->
          <div
            class="absolute bottom-6 left-1/2 -translate-x-1/2 px-4 py-1.5 bg-surface-modal/90 backdrop-blur-md border border-border-strong rounded-full text-text-primary text-xs font-mono font-bold"
          >
            {{ activeIndex + 1 }} / {{ galleryImages.length }}
          </div>

          <!-- Close -->
          <button
            type="button"
            class="absolute top-4 right-4 w-10 h-10 rounded-full bg-surface-elevated hover:bg-surface-hover backdrop-blur-md border border-border-strong flex items-center justify-center text-text-primary transition-all"
            (click)="closeLightbox()"
            aria-label="Close"
          >
            <i class="fa-solid fa-times text-sm"></i>
          </button>

          <!-- Prev -->
          @if (galleryImages.length > 1) {
            <button
              type="button"
              class="absolute left-4 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-surface-elevated hover:bg-surface-hover backdrop-blur-md border border-border-strong flex items-center justify-center text-text-primary transition-all active:scale-90"
              (click)="prev()"
              aria-label="Previous"
            >
              <i class="fa-solid fa-chevron-left text-sm"></i>
            </button>

            <!-- Next -->
            <button
              type="button"
              class="absolute right-4 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-surface-elevated hover:bg-surface-hover backdrop-blur-md border border-border-strong flex items-center justify-center text-text-primary transition-all active:scale-90"
              (click)="next()"
              aria-label="Next"
            >
              <i class="fa-solid fa-chevron-right text-sm"></i>
            </button>
          }

          <!-- Thumbnails strip -->
          @if (galleryImages.length > 1) {
            <div
              class="absolute bottom-14 left-1/2 -translate-x-1/2 flex gap-2 px-4 py-2 bg-surface-modal/90 backdrop-blur-md rounded-2xl border border-border-strong max-w-[90vw] overflow-x-auto"
            >
              @for (img of galleryImages; track img; let i = $index) {
                <button
                  type="button"
                  class="shrink-0 w-12 h-9 rounded-lg overflow-hidden border-2 transition-all"
                  [class.border-primary]="i === activeIndex"
                  [class.border-transparent]="i !== activeIndex"
                  [class.opacity-50]="i !== activeIndex"
                  (click)="activeIndex = i"
                >
                  <img [src]="img" class="w-full h-full object-cover" />
                </button>
              }
            </div>
          }
        </div>
      </div>
    }
  `,
  styles: [
    `
      :host {
        display: block;
      }
    `,
  ],
})
export class ProjectGalleryComponent {
  @Input() galleryImages: string[] = [];

  lightboxOpen = false;
  activeIndex = 0;

  openLightbox(index: number): void {
    this.activeIndex = index;
    this.lightboxOpen = true;
  }

  closeLightbox(): void {
    this.lightboxOpen = false;
  }

  prev(): void {
    this.activeIndex =
      this.activeIndex === 0 ? this.galleryImages.length - 1 : this.activeIndex - 1;
  }

  next(): void {
    this.activeIndex =
      this.activeIndex === this.galleryImages.length - 1 ? 0 : this.activeIndex + 1;
  }

  @HostListener('document:keydown', ['$event'])
  onKeydown(event: KeyboardEvent): void {
    if (!this.lightboxOpen) return;
    if (event.key === 'Escape') this.closeLightbox();
    if (event.key === 'ArrowLeft') this.prev();
    if (event.key === 'ArrowRight') this.next();
  }
}
